import { createContext, useContext, useEffect, useState } from 'react'

/** Everything the field screens share: who is acting, the day, navigation, GPS, network, and the stop list. */
export const FieldContext = createContext(null)
export const useField = () => useContext(FieldContext)

/**
 * Position of the worker. Three demo modes (switch in the Demo controls): 'sim' (default, follows the stops),
 * 'real' (the device GPS through the browser) and 'denied' (simulates a phone with location off, to test the manual
 * flow). In production only the real GPS exists.
 */
export const useWorkerPosition = (field, actorKey, firstStop) => {
    const [real, setReal] = useState(null)
    const [error, setError] = useState('')
    const mode = field.gps
    useEffect(() => {
        if (mode !== 'real') return undefined
        if (typeof navigator === 'undefined' || !navigator.geolocation) { setError('This device has no location service.'); return undefined }
        setError('')
        const id = navigator.geolocation.watchPosition(
            (p) => { setReal({ lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy }); setError('') },
            (e) => setError(e && e.code === 1 ? 'Location permission was denied.' : 'Could not get your location.'),
            { enableHighAccuracy: true, maximumAge: 15000, timeout: 20000 }
        )
        return () => navigator.geolocation.clearWatch(id)
    }, [mode])
    const last = field.positions[actorKey]
    if (mode === 'denied') return { pos: null, source: 'denied', error: 'Location is turned off on this device.' }
    if (mode === 'real') return { pos: real, source: real ? 'real' : 'waiting', error: error }
    if (last) return { pos: { lat: last.lat, lng: last.lng }, source: 'sim', error: '' }
    if (firstStop) return { pos: { lat: firstStop.lat - 0.07, lng: firstStop.lng - 0.06 }, source: 'sim', error: '' }
    return { pos: null, source: 'sim', error: '' }
}

/** Where the worker is at the moment of a check-in (simulated: at the door, or "away" to test the far-from-store flag). */
export const positionAtCheckIn = (field, target, livePos) => {
    if (field.gps === 'denied') return null
    if (field.gps === 'real') return livePos
    if (field.away) return { lat: target.lat + 0.022, lng: target.lng + 0.012 }
    return { lat: target.lat + 0.0004, lng: target.lng - 0.0003 }
}
